import { userFacingError } from '../utils/userFacingError';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { socialMediaService } from '../services/socialMediaService';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { loadBackgroundImage, loadCoverImage as loadCoverImageFile } from '../utils/imageLoader';
import toast from 'react-hot-toast';

type GeneratorVariant = 'profile' | 'sharepic';

interface ImageGeneratorBaseProps {
  backgroundUrl: string;
  backgroundName: string;
  variant: GeneratorVariant;
}

const variantConfig: Record<GeneratorVariant, {
  generatingText: string;
  previewTitle: string;
  downloadButtonText: string;
  missingDownloadText: string;
  successGenerateText: string;
  successDownloadText: string;
  downloadPrefix: string;
  imageAlt: string;
}> = {
  profile: {
    generatingText: 'Generiere Profilbild automatisch...',
    previewTitle: 'Generiertes Profilbild:',
    downloadButtonText: 'Profilbild herunterladen',
    missingDownloadText: 'Kein Profilbild zum Herunterladen verfügbar',
    successGenerateText: 'Profilbild erfolgreich generiert!',
    successDownloadText: 'Profilbild heruntergeladen!',
    downloadPrefix: 'profilbild',
    imageAlt: 'Generiertes Profilbild'
  },
  sharepic: {
    generatingText: 'Generiere Sharepic automatisch...',
    previewTitle: 'Generiertes Sharepic:',
    downloadButtonText: 'Sharepic herunterladen',
    missingDownloadText: 'Kein Sharepic zum Herunterladen verfügbar',
    successGenerateText: 'Sharepic erfolgreich generiert!',
    successDownloadText: 'Sharepic heruntergeladen!',
    downloadPrefix: 'sharepic',
    imageAlt: 'Generiertes Sharepic'
  }
};

const ImageGeneratorBase: React.FC<ImageGeneratorBaseProps> = ({
  backgroundUrl,
  backgroundName,
  variant
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [coverImageElement, setCoverImageElement] = useState<HTMLImageElement | null>(null);

  const config = variantConfig[variant];

  // Cover-Bild von der API laden
  const loadCoverImage = async () => {
    try {
      const latestIssue = await socialMediaService.getLatestIssue();
      // Extrahiere die Cover-Nummer aus der issue
      const coverNumber = latestIssue.number;
      if (coverNumber) {
        setCoverImage(coverNumber);
        setError(null);
      } else {
        throw new Error('Keine Cover-Nummer in der API-Antwort gefunden');
      }
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      setError(`Fehler beim Laden des Cover-Bildes: ${errorMessage}`);
      toast.error(`Cover-Bild konnte nicht geladen werden: ${errorMessage}`);
    }
  };

  // Bild generieren
  const generateImage = useCallback(async () => {
    if (!backgroundUrl || !coverImage) {
      toast.error('Hintergrund und Cover-Bild sind erforderlich');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      // Beide Bilder laden
      const bgImg = await loadBackgroundImage(backgroundUrl);
      const coverImg = await loadCoverImageFile(coverImage, 'Cover-Bild konnte nicht geladen werden');

      setBackgroundImage(bgImg);
      setCoverImageElement(coverImg);

      // Canvas-Dimensionen setzen (Hintergrund-Dimensionen)
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas nicht gefunden');
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas-Kontext nicht verfügbar');
      }

      // Canvas auf Hintergrund-Größe setzen
      canvas.width = bgImg.width;
      canvas.height = bgImg.height;

      // Hintergrund zeichnen
      ctx.drawImage(bgImg, 0, 0, bgImg.width, bgImg.height);

      // Cover-Bild skalieren und zentrieren
      const coverAspectRatio = coverImg.width / coverImg.height;

      // Cover auf Hintergrund-Höhe skalieren
      const scaledCoverHeight = bgImg.height;
      const scaledCoverWidth = scaledCoverHeight * coverAspectRatio;

      // Horizontale Zentrierung berechnen
      const coverX = (bgImg.width - scaledCoverWidth) / 2;
      const coverY = 0; // Vertikal oben ausrichten

      // Cover zeichnen
      ctx.drawImage(
        coverImg,
        coverX,
        coverY,
        scaledCoverWidth,
        scaledCoverHeight
      );

      toast.success(config.successGenerateText);
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      setError(errorMessage);
      toast.error(`Fehler beim Generieren: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  }, [backgroundUrl, coverImage, config.successGenerateText]);

  // Bild herunterladen
  const downloadImage = async () => {
    const canvas = canvasRef.current;
    if (!canvas) {
      toast.error(config.missingDownloadText);
      return;
    }

    setIsDownloading(true);

    try {
      // Canvas als Blob konvertieren
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Fehler beim Erstellen der Datei');
          return;
        }

        // Download-Link erstellen
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${config.downloadPrefix}-${backgroundName}-${new Date().toISOString().split('T')[0]}.png`;

        // Download auslösen
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // URL freigeben
        URL.revokeObjectURL(url);

        toast.success(config.successDownloadText);
      }, 'image/png', 1.0);
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      toast.error(`Fehler beim Herunterladen: ${errorMessage}`);
    } finally {
      setIsDownloading(false);
    }
  };

  // Cover-Bild laden und Bild automatisch generieren
  useEffect(() => {
    const initializeAndGenerate = async () => {
      await loadCoverImage();
      // Warte kurz, damit das Cover-Bild geladen ist
      setTimeout(() => {
        if (backgroundUrl && coverImage) {
          generateImage();
        }
      }, 500);
    };

    initializeAndGenerate();
  }, [backgroundUrl, coverImage, generateImage]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm text-red-800">{error}</span>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Status-Anzeige für automatische Generierung */}
        {isGenerating && (
          <div className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-200 text-sm font-medium rounded-md text-gray-700 bg-gray-50">
            <LoadingSpinner size="sm" />
            <span className="ml-2">{config.generatingText}</span>
          </div>
        )}

        {/* Canvas (versteckt) */}
        <canvas
          ref={canvasRef}
          className="hidden"
          style={{ maxWidth: '100%', height: 'auto' }}
        />

        {/* Generiertes Bild anzeigen */}
        {backgroundImage && coverImageElement && (
          <div className="mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{config.previewTitle}</h4>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-center">
                <img
                  src={canvasRef.current?.toDataURL()}
                  alt={config.imageAlt}
                  className="max-w-full h-auto rounded-lg shadow-sm"
                  style={{ maxHeight: '300px' }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Download-Button */}
        {backgroundImage && coverImageElement && (
          <button
            onClick={downloadImage}
            disabled={isDownloading}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDownloading ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Lade herunter...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {config.downloadButtonText}
              </>
            )}
          </button>
        )}

        {/* Vorschau-Info */}
        {backgroundImage && coverImageElement && (
          <div className="text-xs text-gray-500 text-center">
            Dimensionen: {backgroundImage.width} × {backgroundImage.height}px
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageGeneratorBase;
